import { t } from "i18next";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Club } from "../../../../interfaces/club";
import { Athlete } from "../../../../interfaces/athlete";
import { getAthlete } from "../../../../services/athlete";
import { CoachAthleteEdit } from "./athlete-edit";
import moment from 'moment';
import { getClubOptions } from "../../../../services/club";
import avatar from "../../../../assets/images/avatar.svg";
import { CoachAthleteMeasurement } from "./athlete-measurement";
import { getTeamOptions } from "../../../../services/team";
import { Link } from "react-router-dom";
import { CoachAthletePerformance } from "./athlete-performance";
import { LoadingSpinner } from "../../../general/loading-spinner";

export function CoachAthleteList() {
    const [athletes, setAthletes] = useState<Athlete[]>([]);
    const [clubOptions, setClubOptions] = useState<any[]>([]);
    const [teamOptions, setTeamOptions] = useState<any[]>([]);
    const [showEdit, setShowEdit] = useState(false);
    const [showMeasurement, setShowMeasurement] = useState(false);
    const [showPerformance, setShowPerformance] = useState(false);
    const [selectedId, setSelectedId] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreAthletes, setHasMoreAthletes] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClub, setSelectedClub] = useState('');
    const [selectedTeam, setSelectedTeam] = useState('');
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        getClubOptions()
            .then(response => {
                setClubOptions(response.data);
            })
            .catch(error => {
                console.log(error);
            });
        getAthlete('', '', '', '', 'changed_at', 'DESC', 1, 12)
            .then(response => {
                setAthletes(response.data);
                setHasMoreAthletes(response.data.length === 12);
                setCurrentPage(1);
                setLoaded(true);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const club = event.target.club.value;
        let team = '';
        if (event.target.team !== undefined) {
            team = event.target.team.value;
        }

        setSearchTerm(s);
        setSelectedClub(club);
        setSelectedTeam(team);

        getAthlete(s, club, team, '', 'changed_at', 'DESC', 1, 12)
            .then(response => {
                setAthletes(response.data);
                setHasMoreAthletes(response.data.length === 12);
                setCurrentPage(1);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        const newAthletes = athletes.map((m: Athlete) => {
            if (m.id == changedAthlete.id) {
                return changedAthlete;
            } else {
                return m;
            }
        });
        setAthletes(newAthletes);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    const handleMeasurement = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowMeasurement(true);
    }

    const handleMeasurementOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        setShowMeasurement(false);
    }

    const handleMeasurementOnClose = () => {
        setSelectedId(0);
        setShowMeasurement(false);
    }

    const handlePerformance = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowPerformance(true);
    }

    const handlePerformanceOnSave = (changedAthlete: Athlete) => {
        setSelectedId(0);
        setShowPerformance(false);
    }

    const handlePerformanceOnClose = () => {
        setSelectedId(0);
        setShowPerformance(false);
    }

    const handleClubChange = (event: any) => {
        if (event.target.value.length > 0) {
            getTeamOptions(event?.target.value)
                .then((response: any) => {
                    setTeamOptions(response.data.map((t: any) => {
                        return {
                            value: t.id,
                            label: t.name
                        }
                    }))
                })
                .catch((error: any) => {
                    console.log(error);
                });
        } else {
            setTeamOptions([]);
        }
    }

    const handleLoadMore = () => {
        if (isLoadingMore || !hasMoreAthletes) return;

        setIsLoadingMore(true);
        const nextPage = currentPage + 1;

        getAthlete(searchTerm, selectedClub, selectedTeam, '', 'changed_at', 'DESC', nextPage, 12)
            .then(response => {
                setAthletes(prevAthletes => [...prevAthletes, ...response.data]);
                setCurrentPage(nextPage);
                setHasMoreAthletes(response.data.length === 12);
                setIsLoadingMore(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoadingMore(false);
            });
    }

    return (
        <div className="athletes-list-container">
            <h1>
                { t('athletes')}
            </h1>
            { !loaded && <LoadingSpinner /> }
            { loaded &&
                <div className="filters-container">
                <Form onSubmit={ handleSearchSubmit }>
                    <Form.Group className="field-search" controlId="search">
                        <Form.Control type="text" placeholder={ t("Write to search") } />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="club_id">
                        <Form.Select aria-label={ t("club") } name="club" onChange={ handleClubChange }>
                            <option value="">{ t("select_club") }</option>
                            { clubOptions.map((c: Club) => {
                                return <option key={ 'club-' + c.id } value={ c.id }>{ c.name }</option>;
                            })}
                        </Form.Select>
                    </Form.Group>
                    { teamOptions.length > 0 &&
                        <Form.Group className="mb-3" controlId="team">
                            <Form.Select aria-label={ t("team") } name="team">
                                <option value="">{ t("select_team") }</option>
                                { teamOptions.map((t: any) => {
                                    return <option key={ 'team-' + t.value } value={ t.value }>{ t.label }</option>;
                                })}
                            </Form.Select>
                        </Form.Group>
                    }
                    <div className="actions">
                        <Button variant="primary" type="submit">
                            { t("search") }
                        </Button>
                    </div>
                </Form>
                </div>
            }
            { loaded &&
                <div className="grid-container">
                <Row>
                    { athletes.map((a: Athlete) => {
                        return <Col key={ 'athlete-' + a.id } xs={12} md={6} lg={4}>
                            <div className="athlete-card">
                                <div className="heading">
                                    <div className="avatar">
                                        { a.avatar.length > 0 &&
                                            <img src={ a.avatar[0].url } alt={ a.name } />
                                        }
                                        { a.avatar.length == 0 &&
                                            <img src={ avatar } alt={ a.name } />
                                        }
                                    </div>
                                    <div className="meta">
                                        <div className="name">
                                            <Link to={ '/athletes/' + a.id + '/view' } title={ a.name }>{ a.name }</Link>
                                        </div>
                                        { a.birthdate && (
                                            <div className="years">
                                                { moment(new Date()).diff(a.birthdate, 'years') + ' ' + t("years").toLowerCase() }
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="actions">
                                    <a href="#" className="icon-link edit" title={ t("edit") } onClick={ handleEdit } data-id={ a.id }>
                                        <i className="bi bi-pencil-square"></i>
                                    </a>
                                    <a href="#" className="icon-link measurement" title={ t("measurement") } onClick={ handleMeasurement } data-id={ a.id }>
                                        <i className="bi bi-clipboard-data"></i>
                                    </a>
                                    <a href="#" className="icon-link performance" title={ t("performance") } onClick={ handlePerformance } data-id={ a.id }>
                                        <i className="bi bi-bar-chart-steps"></i>
                                    </a>
                                </div>
                            </div>
                        </Col>
                    })}
                </Row>
                { hasMoreAthletes && (
                    <Row className="mt-4">
                        <Col className="text-center">
                            <Button
                                variant="outline-primary"
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                            >
                                {isLoadingMore ? t('loading') + '...' : t('Load more')}
                            </Button>
                        </Col>
                    </Row>
                )}
                </div>
            }
            { showEdit && (selectedId != 0) &&
                <CoachAthleteEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
            }
            { showMeasurement && (selectedId != 0) &&
                <CoachAthleteMeasurement id={ selectedId } onSave={ handleMeasurementOnSave } onClose={ handleMeasurementOnClose } />
            }
            { showPerformance && (selectedId != 0) &&
                <CoachAthletePerformance id={ selectedId } onSave={ handlePerformanceOnSave } onClose={ handlePerformanceOnClose } />
            }
        </div>

    )
}